import {
  Modal,
  ModalOverlay,
  ModalContent,
  Text,
  Flex,
  ModalBody,
  Heading,
  Button,
  FormControl,
  ModalHeader,
  ModalCloseButton,
} from '@chakra-ui/react';
import { userApi, UserType } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { useRegexSearch } from '@texas/components/shared/hooks/useRegexSearch';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { useActiveSession } from '@texas/hooks/useSession';
import { moveDownRotateAnimation } from '@texas/resources/animations/animations';
import {
  compareEnum,
  ensureEnumNumber,
} from '@texas/utils/helpers/enumHelpers';
import { userTypeFriendlyName } from '@texas/utils/helpers/roleHelpers';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends SharedDisclosureProps {
  customerId: number;
  branchId?: number;
  userType: UserType | null;
  onSelect: (id: number) => void;
  filterIds: number[];
}

export function SelectUserModal({
  isOpen,
  onClose,
  branchId,
  userType,
  onSelect,
  filterIds,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={true}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('customerUser.select')}</ModalHeader>
        <ModalCloseButton />
        <ModalInner
          branchId={branchId}
          userType={userType}
          onSelect={onSelect}
          filterIds={filterIds}
        />
      </ModalContent>
    </Modal>
  );
}

function ModalInner({
  branchId,
  userType,
  onSelect,
  filterIds,
}: {
  branchId?: number;
  userType: UserType | null;
  onSelect: (id: number) => void;
  filterIds: number[];
}) {
  const { data, refetch, loading } = useApiResource(userApi.getAllInternal);
  const { t } = useTranslation();
  const session = useActiveSession();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { search, setSearch, regexSearch } = useRegexSearch();

  const recommendedTypes = useMemo(() => {
    if (!data) return [];

    const regexFilteredData = data.filter(
      (x) =>
        x.name.toLowerCase().match(regexSearch) ??
        x.email.toLowerCase().match(regexSearch),
    );

    if (!branchId) {
      return regexFilteredData.filter((x) =>
        compareEnum(UserType, x.userType, userType),
      );
    }

    return regexFilteredData.filter(
      (x) =>
        x.branchId === branchId && compareEnum(UserType, x.userType, userType),
    );
  }, [branchId, data, regexSearch, userType]);

  return (
    <ModalBody maxH="700px" minH="700px" overflow="auto" pt={0}>
      {loading ? (
        <SpinnerWithLabel label={t('general.loadingData')} />
      ) : (
        <Flex flexDir="column" gap={2}>
          <FormControl
            pb={2}
            pos="sticky"
            top={0}
            zIndex={1}
            bg="texas.bg.900"
            pt={2}
            _light={{
              bg: 'white',
            }}
          >
            <SearchFilterInput
              debounceDelay={0}
              autoFocus={true}
              placeholder={t('customerUser.searchUser')}
              value={search}
              onChange={(s) => setSearch(s)}
            />
          </FormControl>
          {recommendedTypes.length > 0 && (
            <>
              <Flex gap={2} align="center">
                <Icons.ThumbUp
                  animation={moveDownRotateAnimation(1000)}
                  boxSize={defaultIconSize}
                />
                <Heading fontSize="xl">
                  {t('article.productDevelopers.recommendedUsers')}
                </Heading>
              </Flex>
              {recommendedTypes
                .filter((x) => x.name.toLowerCase().match(regexSearch))
                .map((x) => {
                  const branch = session.branches.find(
                    (x2) => x2.id === x.branchId,
                  );
                  return (
                    <Item
                      key={x.id}
                      name={x.name}
                      branch={branch?.name}
                      userType={x.userType}
                      onClick={() => onSelect(x.id)}
                      alreadyAdded={filterIds.includes(x.id)}
                    />
                  );
                })}
            </>
          )}

          <Heading fontSize="xl">
            {t('article.productDevelopers.allUsers')}
          </Heading>
          {data
            ?.filter(
              (x) =>
                x.name.toLowerCase().match(regexSearch) ??
                x.email.toLowerCase().match(regexSearch),
            )
            .map((x) => {
              const branch = session.branches.find(
                (x2) => x2.id === x.branchId,
              );
              return (
                <Item
                  key={x.id}
                  name={x.name}
                  branch={branch?.name}
                  userType={x.userType}
                  onClick={() => onSelect(x.id)}
                  alreadyAdded={filterIds.includes(x.id)}
                />
              );
            })}
        </Flex>
      )}
    </ModalBody>
  );
}

function Item({
  name,
  branch,
  userType,
  onClick,
  alreadyAdded,
}: {
  name: string;
  branch?: string;
  userType: UserType | null;
  onClick: () => void;
  alreadyAdded: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Button
      variant="unstyled"
      textAlign="start"
      h="auto"
      w="auto"
      bg="whiteAlpha.100"
      p={2}
      isDisabled={alreadyAdded}
      onClick={onClick}
      _hover={{
        bg: 'whiteAlpha.200',
      }}
      _light={{
        bg: 'blackAlpha.100',
        _hover: {
          bg: 'blackAlpha.200',
        },
      }}
    >
      <Text fontFamily="Barlow">{name}</Text>
      {branch && <Text fontSize="sm">{branch}</Text>}
      {userType && (
        <Text variant="sub" fontSize="sm">
          {userTypeFriendlyName(ensureEnumNumber(UserType, userType), t)}
        </Text>
      )}
    </Button>
  );
}
